import React, { useEffect, useRef, useState, ComponentType } from 'react';
import { Routes, Route } from "react-router-dom";
import { Button } from "./components/ui/button";
import {TweetMarquee } from "./components/magicui/TweetMarquee";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "./components/ui/card";
import {AnimatedGridPattern} from "./components/magicui/animated-grid-pattern";
import { 
  FileText, 
  Monitor, 
  Mail, 
  TrendingUp, 
  Star, 
  Users,
  LucideProps
} from "lucide-react";
import "./styles/App.css";
import { cn } from "@/lib/utils";

function AnimatedGridPatternDemo() {
  return (
    <div className="relative flex h-[1000px] w-full items-center justify-center overflow-hidden rounded-lg border bg-background p-20">
      <AnimatedGridPattern
        numSquares={1000}
        maxOpacity={0.3}
        duration={2}
        repeatDelay={0.5}
        className={cn(
          "[mask-image:radial-gradient(500px_circle_at_center,white,transparent)]",
          "inset-x-0 inset-y-[-30%] h-[200%] skew-y-12",
        )}
        squaresClassName="hover:fill-blue-500"

      />
    </div>
  );
}

function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Arrow animation
    class Arrow {
      x: number;
      y: number;
      speed: number;
      opacity: number;
      size: number;
      delay: number;
      time: number;

      constructor() {
        this.x = Math.random() * canvas.width / 2 + canvas.width / 4;
        this.y = canvas.height / 2;
        this.speed = 0.5 + Math.random() * 1.5;
        this.opacity = 0;
        this.size = 5 + Math.random() * 4;
        this.delay = Math.random() * 100;
        this.time = 0;
      }

      update() {
        this.time++;
        if (this.time > this.delay) {
          this.opacity = Math.min(1, this.opacity + 0.01);
          this.y -= this.speed;
          this.speed *= 1.02;
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.time <= this.delay) return;
        
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(-Math.PI / 2);
        
        // Draw arrow
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-this.size * 0.7, -this.size * 0.7);
        ctx.lineTo(0, -this.size * 1.5);
        ctx.lineTo(this.size * 0.7, -this.size * 0.7);
        ctx.closePath();
        
        ctx.fillStyle = `rgba(147, 51, 234, ${this.opacity})`;
        ctx.fill();
        ctx.restore();
      }
    }

    const arrows: Arrow[] = [];
    for (let i = 0; i < 20; i++) {
      arrows.push(new Arrow());
    }

    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      arrows.forEach(arrow => {
        arrow.update();
        arrow.draw(ctx);
        
        if (arrow.y < -50) {
          const index = arrows.indexOf(arrow);
          if (index > -1) {
            arrows.splice(index, 1);
            arrows.push(new Arrow());
          }
        }
      });
      
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="text-left">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              From -0.5 to 0.5:
              <span className="text-purple-600 block">The Ignite Fund</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-xl">
              Creating Zero Risk Enviornments For The World's Brightest. We Help Create The Sandbox For Your Imagination.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg">
                Apply for API Credits
              </Button>
              <Button variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-6 text-lg">
                Learn About Our Mission
              </Button>
            </div>
          </div>
          
          {/* Right side - Animation */}
          <div className="relative h-[500px] w-full">
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

const OurWhySection = () => {
  const sectionRef = React.useRef(null);
  return (
    <section ref={sectionRef} className="py-20 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-wider text-purple-600 uppercase">
            Technology Made Possible
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-6">
            Our Why
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Empowering the next generation of builders with the tools they need to succeed.
          </p>
        </div>
        {/* 3D Tweet Marquee */}
        <div className="mb-20">
          <TweetMarquee/>
        </div>

        <div className="mb-20">
        <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-8 md:flex-nowrap">
          <Card className="h-[200px] w-full bg-white border border-purple-600 dark:border-purple-700 rounded-lg p-4 hover:bg-purple-50 dark:hover:bg-purple-900 transition duration-150 ease-in-out">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-purple-600 dark:text-white">
                A Disconnect in the Status Quo
              </CardTitle>
            </CardHeader>
            <CardContent className="mt-2 h-full">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We're creating a fund that generously gives and asks nothing in return. Talent deserves the freedom to explore what you want to do and, more importantly, who you want to be.
              </p>
            </CardContent>
          </Card>
          <Card className="h-[200px] w-full bg-white border border-purple-600 dark:border-purple-700 rounded-lg p-4 hover:bg-purple-50 dark:hover:bg-purple-900 transition duration-150 ease-in-out">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-purple-600 dark:text-white">
                Learn By Doing
              </CardTitle>
            </CardHeader>
            <CardContent className="mt-2 h-full">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                With the current speed of innovation, going to a great university or working at a tech firm won't prepare the future's brightest. Instead, we believe in the principles of constant curiosity, bravery, and iteration.
              </p>
            </CardContent>
          </Card>
          <Card className="h-[200px] w-full bg-white border border-purple-600 dark:border-purple-700 rounded-lg p-4 hover:bg-purple-50 dark:hover:bg-purple-900 transition duration-150 ease-in-out">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-purple-600 dark:text-white">
                The Invitation of Risk
              </CardTitle>
            </CardHeader>
            <CardContent className="mt-2 h-full">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The pursuit of a zero cost building enviornment doesn't mean we want builders to take less risk. Instead, we invite builders to dare and to imagine.
              </p>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </section>
  );
};

const TheMission = () => {
  
}

const Application = () => {
  interface TimelineItem {
    id: number
    title: string
    description: string
    icon: ComponentType<LucideProps>
  }
  
  const timelineItems: TimelineItem[] = [
    {
      id: 1,
      title: "Apply before December 15th",
      description: "Submit your application with your project idea and background. No lengthy essays required.",
      icon: FileText,
    },
    {
      id: 2,
      title: "Automated Review System",
      description:
        "Our AI automatically reads every application to ensure fair and comprehensive evaluation of all candidates.",
      icon: Monitor,
    },
    {
      id: 3,
      title: "Hear back within a week",
      description: "If accepted, you'll receive notification and access to our exclusive fellow resources and community.",
      icon: Mail,
    },
    {
      id: 4,
      title: "One month of building",
      description: "Get access to our API credits and build something amazing with our support and guidance.",
      icon: TrendingUp,
    },
    {
      id: 5,
      title: "Submit project application",
      description: "Show us what you've accomplished during your fellowship month. This is your moment to shine.",
      icon: Star,
    },
    {
      id: 6,
      title: "Join our global community",
      description: "Connect with fellows from all batches and continue growing in our supportive ecosystem.",
      icon: Users,
    },
  ]
  
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set())
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observers = itemRefs.current.map((ref, index) => {
      if (!ref) return null

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, index]))
          }
        },
        {
          threshold: 0.3,
          rootMargin: "-50px 0px",
        },
      )

      observer.observe(ref)
      return observer
    })

    return () => {
      observers.forEach((observer) => observer?.disconnect())
    }
  }, [])

  return (
    <section className="py-20">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-sm font-semibold tracking-wider text-purple-600 uppercase">
            Ignite Your Excellence
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-6">
            Apply for Batch <span className="text-purple-600"> I </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Every month we run a batch. The top applicants get our assistance with one ask: at the end of the batch,
            they show us what they have accomplished.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-200 via-purple-400 to-purple-600"></div>

          {/* Timeline items */}
          <div className="space-y-12">
            {timelineItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.id}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`relative flex items-start transition-all duration-700 ease-out ${
                    visibleItems.has(index) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  {/* Timeline dot */}
                  <div
                    className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full border-4 border-white shadow-lg transition-all duration-500 ${
                      visibleItems.has(index) ? "bg-purple-500 scale-100" : "bg-gray-300 scale-75"
                    }`}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Content card */}
                  <Card
                    className={`ml-8 flex-1 transition-all duration-700 hover:shadow-xl ${
                      visibleItems.has(index) ? "shadow-lg border-purple-200" : "shadow-md border-gray-200"
                    }`}
                  >
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Join the Next Batch?</h3>
            <p className="text-purple-100 mb-6">
              This process is run by AI. Each applicant is evaluated by AI & their submissions are read by humans.
            </p>
            <a href="https://forms.gle/HiBQ6vaWto3AiRMR8" target="_blank" rel="noopener noreferrer" className="block">
              <Button
                className="bg-white text-purple-600 font-semibold px-8 py-3 rounded-lg hover:bg-purple-50 transition-colors duration-200 shadow-lg"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("https://forms.gle/HiBQ6vaWto3AiRMR8", "_blank");
                }}
              >
                Start Your Application
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default function App() {
  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/" element={
          <>
          <div className="relative">
            <div className="fixed inset-0 -z-10">
              <AnimatedGridPatternDemo/>
            </div>
              <HeroSection />
              <OurWhySection />
              <Application />
          </div>
          </>
        } />
      </Routes>
    </div>
  );
}